"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PopLoading } from "@/components/pop-ui/pop-loading";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useCfTurnstile } from "@/hooks/useCfTurnstile";
import { useTurnstile } from "@/providers/turnstile-provider";
interface CfInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  infoText?: string; // Use \n for new lines
  containerId: string; // Turnstile container id inside dialog
  maxWidthClassName?: string; // Optional override for dialog width
  onTurnstileSuccess?: (token: string) => void; // Callback when Turnstile verification succeeds
  onTurnstileError?: () => void; // Callback when Turnstile verification fails
  errorMessage?: string; // Error message for turnstile failures
  language?: string; // Optional Cloudflare-supported language (e.g., 'zh', 'zh-cn', 'zh-tw', 'en')
}
export function CfInfoDialog({
  open,
  onOpenChange,
  title,
  infoText,
  containerId,
  onTurnstileSuccess,
  onTurnstileError,
  errorMessage,
  language
}: CfInfoDialogProps) {
  const {
    t
  } = useTranslation();
  const [hasWidgetDom, setHasWidgetDom] = useState(false);

  // Check global SDK state
  const {
    sdkError
  } = useTurnstile();

  // Use Cloudflare Turnstile hook internally (let hook decide language unless explicitly provided)
  const {
    renderTurnstile,
    formatToken,
    isTurnstileEnabled,
    isWidgetReady,
    removeTurnstile
  } = useCfTurnstile({
    containerId,
    ...(language && {
      language
    }),
    errorMessage: errorMessage || t("error.operationFailedRefreshOrTryLater"),
    onError: onTurnstileError
  });

  // Render Turnstile when dialog opens (ensure container exists and is visible)
  useEffect(() => {
    if (open && isTurnstileEnabled && isWidgetReady) {
      let cancelled = false;
      let tries = 0;
      setHasWidgetDom(false);
      const isVisible = (el: HTMLElement | null) => {
        if (!el) return false;
        const style = typeof window !== "undefined" ? window.getComputedStyle(el) : null as any;
        if (!style) return false;
        // Only ensure it's not hidden via display/visibility; size can be 0 before render
        return style.display !== "none" && style.visibility !== "hidden";
      };
      const tryRender = () => {
        if (cancelled) return;
        const el_0 = typeof document !== "undefined" ? document.getElementById(containerId) : null;
        if (isVisible(el_0)) {
          void renderTurnstile();
          return;
        }
        if (tries++ < 60) requestAnimationFrame(tryRender);
      };
      requestAnimationFrame(tryRender);

      // Detect when widget DOM appears and stop showing loader
      const checkDom = () => {
        const root = document.getElementById(containerId);
        if (!root) return false;
        return Boolean(root.childElementCount > 0 || root.querySelector("iframe") || root.querySelector(".cf-turnstile"));
      };
      const domInterval = window.setInterval(() => {
        if (checkDom()) {
          setHasWidgetDom(true);
          window.clearInterval(domInterval);
        }
      }, 100);
      return () => {
        cancelled = true;
        window.clearInterval(domInterval);
      };
    }
    // Clean up widget when dialog closes
    return () => {
      if (!open) {
        try {
          removeTurnstile();
        } catch {}
      }
    };
  }, [open, renderTurnstile, isTurnstileEnabled, isWidgetReady, language, containerId, removeTurnstile]);

  // Reset loader flag when dialog closes
  useEffect(() => {
    if (!open) setHasWidgetDom(false);
  }, [open]);

  // Handle Turnstile completion via global callback
  useEffect(() => {
    if (!open || !isTurnstileEnabled) return;

    // Create a global callback that Turnstile can call
    const turnstileCallback = (token: string) => {
      onOpenChange(false);
      const formattedToken = formatToken(token);
      onTurnstileSuccess?.(formattedToken);
    };

    // Create a global error callback that Turnstile can call
    const turnstileErrorCallback = () => {
      onOpenChange(false); // Close dialog on error
      onTurnstileError?.(); // Call parent error handler
    }

    // Attach the callbacks to window for Turnstile to call
    ;
    (window as any).turnstileSuccessCallback = turnstileCallback;
    (window as any).turnstileErrorCallback = turnstileErrorCallback;
    return () => {
      delete (window as any).turnstileSuccessCallback;
      delete (window as any).turnstileErrorCallback;
      // Ensure widget is removed when dialog unmounts/closes
      try {
        removeTurnstile();
      } catch {}
    };
  }, [open, onOpenChange, onTurnstileSuccess, onTurnstileError, isTurnstileEnabled, formatToken, removeTurnstile]);

  // Listen for Turnstile success and close dialog
  useEffect(() => {
    if (!open || !isTurnstileEnabled) return;
    const checkTurnstileSuccess = () => {
      // Try multiple selectors to find the Turnstile token
      const tokenSelectors = [".cf-turnstile input[name='cf-turnstile-response']", "input[name='cf-turnstile-response']", "[name='cf-turnstile-response']"];
      let token_0: HTMLInputElement | null = null;
      for (const selector of tokenSelectors) {
        token_0 = document.querySelector(selector) as HTMLInputElement;
        if (token_0?.value) break;
      }
      if (token_0?.value) {
        // Turnstile verification completed, close dialog and notify parent
        onOpenChange(false);
        const formattedToken_0 = formatToken(token_0.value);
        onTurnstileSuccess?.(formattedToken_0);
      }
    };

    // Check every 300ms for Turnstile completion
    const interval = setInterval(checkTurnstileSuccess, 300);
    return () => clearInterval(interval);
  }, [open, onOpenChange, onTurnstileSuccess, onTurnstileError, isTurnstileEnabled, formatToken]);

  // Don't render if turnstile is not enabled or SDK failed to load
  if (!isTurnstileEnabled || sdkError) {
    return null;
  }
  return <Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className={`max-w-108! md:w-108 w-[85%] overflow-hidden p-0 [&>button]:hidden gap-0`} onInteractOutside={e => e.preventDefault()}>
				<div className="p-5 md:pt-12 md:px-12 md:pb-3 w-full ">
					<DialogTitle className="text-center text-xs font-bold text-[#010101] md:text-sm pb-4">
						{title ?? t("captcha.infoDialog.title")}
					</DialogTitle>
					<DialogDescription className="sr-only">
						{t("captcha.infoDialog.description")}
					</DialogDescription>
					<div className="flex justify-center items-center min-h-16 md:min-h-24 relative">
						{open && !hasWidgetDom && <div className="absolute inset-0 flex items-center justify-center">
								<PopLoading />
							</div>}
						<div id={containerId} className="  flex items-center justify-center " />
					</div>
				</div>
				{/* Bottom section */}
				<div className="bg-gray-50 p-4 text-px10 leading-6 whitespace-pre-line text-[#374151] md:px-12 md:py-5 md:text-sm ">
					<div className="text-[#333] font-['DINPro-Regular']">
						{infoText ?? t("captcha.infoDialog.infoText")}
					</div>
					<div className="mt-3 text-black font-['DINPro-Bold'] font-bold">
						{t("captcha.infoDialog.troubleText")}
					</div>
				</div>
			</DialogContent>
		</Dialog>;
}
export default CfInfoDialog;